import * as React from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/dashboard/AppSidebar";
import { KeuanganNavDropdown } from "@/components/dashboard/KeuanganNavDropdown";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Loader2, User, Key, LogOut, Sun, Moon, ChevronDown } from "lucide-react";
import { Link } from "wouter";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [, setLocation] = useLocation();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [logoutError, setLogoutError] = React.useState<string | null>(null);
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutDialog(false);
    setIsLoggingOut(true);
    setLogoutError(null);

    try {
      await logout();
      setLocation("/login");
    } catch (error) {
      console.error("Logout error:", error);
      setLogoutError(error instanceof Error ? error.message : "Gagal melakukan logout");
      setTimeout(() => setLocation("/login"), 2000);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar
        onLogoutClick={handleLogoutClick}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
      />
      <SidebarInset>
        <header className="sticky top-0 z-40 shrink-0 border-b bg-background/95 pt-[env(safe-area-inset-top,0px)] backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-14 items-center gap-2 pl-[max(1rem,env(safe-area-inset-left,0px))] pr-[max(1rem,env(safe-area-inset-right,0px))]">
            <SidebarTrigger className="-ml-1" />
            <KeuanganNavDropdown className="flex min-w-0 shrink-0 items-center" />
            <div className="min-w-2 flex-1" />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              title={theme === "dark" ? "Mode terang" : "Mode gelap"}
            >
              {theme === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <User className="size-4" />
                  <span className="hidden max-w-[140px] truncate text-sm sm:inline">
                    {user?.name || user?.email || "User"}
                  </span>
                  <ChevronDown className="size-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link href="/user" className="flex cursor-pointer items-center gap-2">
                    <User className="size-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/user#ubah-password" className="flex cursor-pointer items-center gap-2">
                    <Key className="size-4" />
                    Ubah Password
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {logoutError && (
                  <div className="px-2 py-1.5 text-xs text-destructive">{logoutError}</div>
                )}
                <DropdownMenuItem
                  onClick={handleLogoutClick}
                  disabled={isLoggingOut}
                  className="text-destructive focus:text-destructive"
                >
                  {isLoggingOut ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <LogOut className="mr-2 size-4" />
                  )}
                  Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="flex-1 overflow-auto pb-[env(safe-area-inset-bottom,0px)]">{children}</div>
      </SidebarInset>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Logout</DialogTitle>
            <DialogDescription>
              Apakah Anda yakin ingin keluar? Anda akan perlu login kembali untuk mengakses dashboard.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLogoutDialog(false)}
              disabled={isLoggingOut}
            >
              Batal
            </Button>
            <Button variant="destructive" onClick={handleLogoutConfirm} disabled={isLoggingOut}>
              {isLoggingOut ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Keluar...
                </>
              ) : (
                "Keluar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
